import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Ruler } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardBody, Table, Button, Modal, ConfirmDialog, toast
} from '../../components/common';
import { catalogApi } from '../../services/api';
import UOMForm from './UOMForm';
import './UOM.css';

export default function UOMList() {
    const queryClient = useQueryClient();
    const [showForm, setShowForm] = useState(false);
    const [editingUOM, setEditingUOM] = useState(null);
    const [deleteUOM, setDeleteUOM] = useState(null);

    const { data, isLoading } = useQuery({
        queryKey: ['uom'],
        queryFn: () => catalogApi.listUOM(),
        select: (res) => res.data?.data || []
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => catalogApi.deleteUOM?.(id) || Promise.resolve(),
        onSuccess: () => {
            toast.success('Unit of Measure deleted successfully');
            queryClient.invalidateQueries(['uom']);
            setDeleteUOM(null);
        },
        onError: () => {
            toast.error('Failed to delete. UOM might be in use.');
        }
    });

    const handleEdit = (uom) => {
        setEditingUOM(uom);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingUOM(null);
    };

    const columns = [
        {
            key: 'code',
            title: 'Code',
            dataIndex: 'code',
            width: 100,
            render: (code) => <span className="uom-code">{code}</span>
        },
        {
            key: 'name',
            title: 'Name',
            dataIndex: 'name'
        },
        {
            key: 'description',
            title: 'Description',
            dataIndex: 'description',
            render: (desc) => desc || '-'
        },
        {
            key: 'actions',
            title: '',
            width: 100,
            align: 'right',
            render: (_, row) => (
                <div className="uom-actions">
                    <button onClick={() => handleEdit(row)} title="Edit">
                        <Edit2 size={14} />
                    </button>
                    <button onClick={() => setDeleteUOM(row)} className="danger" title="Delete">
                        <Trash2 size={14} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="uom-page">
            <PageHeader
                title="Units of Measure"
                subtitle="Manage units of measurement for catalog items"
                breadcrumbs={[
                    { label: 'Catalog', path: '/catalog' },
                    { label: 'Units of Measure' }
                ]}
                actions={
                    <Button icon={Plus} onClick={() => setShowForm(true)}>
                        Add UOM
                    </Button>
                }
            />

            <Card>
                <Table
                    columns={columns}
                    data={data || []}
                    loading={isLoading}
                    emptyText="No units of measure found"
                    rowKey="id"
                />
            </Card>

            <Modal
                isOpen={showForm}
                onClose={handleCloseForm}
                title={editingUOM ? 'Edit Unit of Measure' : 'Add Unit of Measure'}
                size="sm"
            >
                <UOMForm
                    uom={editingUOM}
                    onSuccess={() => {
                        handleCloseForm();
                        queryClient.invalidateQueries(['uom']);
                    }}
                    onCancel={handleCloseForm}
                />
            </Modal>

            <ConfirmDialog
                isOpen={!!deleteUOM}
                onClose={() => setDeleteUOM(null)}
                onConfirm={() => deleteMutation.mutate(deleteUOM?.id)}
                title="Delete Unit of Measure"
                message={`Are you sure you want to delete "${deleteUOM?.name}"?`}
                confirmText="Delete"
                variant="danger"
                loading={deleteMutation.isPending}
            />
        </div>
    );
}
